import { CircularProgress, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'

import { MUIExternalLink } from 'components/common/MUIExternalLink'
import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import Icon from 'components/Icon'
import { MonitorDetails } from 'components/status-checks/common/MonitorDetails'
import {
  formatStatusCheckTag,
  getMonitorPageUrl
} from 'components/status-checks/status-checks-form-utils'
import { useGetDatadogMonitor } from 'queries/monitors/use-get-datadog-monitor'
import { getMessageFromResponseOrError } from 'utils/strings'

import type { ServiceStatusCheckFromStatusCheck } from 'types/api/status-checks/status-check'

export function MonitorDetailsDatadog({
  monitorId,
  statusCheck
}: {
  monitorId: string
  statusCheck: ServiceStatusCheckFromStatusCheck
}) {
  const {
    domain,
    name: datadogIntegrationName,
    type: integrationType,
    url: integrationUrl
  } = statusCheck.integration

  const requestParams = {
    datadogIntegrationName,
    ...(domain && domain !== 'US1' && { domain })
  }

  const {
    data: datadogMonitor,
    error: errorFetchingDatadogMonitor,
    isFetching: isFetchingDatadogMonitor,
    isError: isErrorFetchingDatadogMonitor
  } = useGetDatadogMonitor(monitorId, requestParams)

  const monitorName = datadogMonitor?.name

  const monitorTags = useMemo(
    () => datadogMonitor?.tags?.map((tag) => formatStatusCheckTag(tag)),
    [datadogMonitor?.tags]
  )

  const errorMessageFromResponse = getMessageFromResponseOrError(
    errorFetchingDatadogMonitor
  )

  return (
    <MonitorDetails
      header={
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          {monitorName && (
            <TruncatedTextWithTooltip
              tooltipProps={{
                title: monitorName ? `Monitor: ${monitorName}` : ''
              }}
            >
              {monitorName}
            </TruncatedTextWithTooltip>
          )}
          {isFetchingDatadogMonitor && (
            <>
              <CircularProgress size={20} sx={{ color: 'grey.300' }} />
              <Typography sx={{ color: 'grey.700' }} variant="body2">
                Loading Datadog details...
              </Typography>
            </>
          )}
          {isErrorFetchingDatadogMonitor && (
            <Typography color="error" variant="body2">
              Failed to load Datadog monitor details and tags
              {errorMessageFromResponse ? `: ${errorMessageFromResponse}` : '.'}
            </Typography>
          )}
          {integrationUrl && (
            <MUIExternalLink
              href={getMonitorPageUrl(integrationUrl, integrationType)}
            >
              <Icon icon="arrowUpRightFromSquare" />
            </MUIExternalLink>
          )}
        </Stack>
      }
      statusCheck={statusCheck}
      tagsFromMonitor={monitorTags}
    />
  )
}
